import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Shield, Zap, CheckCircle, Users, Target, Eye,
  Award, Briefcase, GraduationCap, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ceoInfo, coreValues, stats, companyInfo } from '../data/mock';

const iconMap = {
  Shield: Shield,
  Zap: Zap,
  CheckCircle: CheckCircle
};

const AboutPage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-32 bg-[#1B4F5D]">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <span className="text-[#408D4D] font-semibold tracking-wider uppercase text-sm">About Us</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6">
              Moving Business Forward, Together
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              A dynamic logistics and trade support company based in Ghana, committed to 
              delivering reliable and efficient shipping solutions across borders.
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#408D4D] font-semibold tracking-wider uppercase text-sm">Our Story</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1B4F5D] mt-3 mb-6">
                Building Seamless Trade Pathways
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  At CitiBiz Connections, we believe that logistics is more than just moving goods—it's 
                  about creating seamless, trustworthy pathways that drive growth and opportunity for 
                  businesses across borders.
                </p>
                <p>
                  As a dynamic logistics and trade support company based in Ghana, our mission is rooted 
                  in reliability, efficiency, and a deep understanding of the unique challenges in cargo 
                  handling, customs clearance, and export logistics.
                </p>
                <p>
                  From our home base in Ghana to destinations across the continent and beyond, we are 
                  proud to support our clients with solutions that are not only compliant and secure 
                  but also tailored to their specific trade needs.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1585713181935-d5f622cc2415?w=800&q=80"
                alt="Cargo Ship"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#408D4D] text-white p-6 rounded-xl shadow-xl">
                <div className="text-4xl font-bold">10+</div>
                <div className="text-sm opacity-90">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Vision */}
            <Card className="bg-white border-0 shadow-xl overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-[#1B4F5D] p-8">
                  <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-4">
                    <Eye className="text-[#408D4D]" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Our Vision</h3>
                </div>
                <div className="p-8">
                  <p className="text-gray-600 leading-relaxed text-lg">
                    Our vision is to become the preferred logistics brand in West Africa, renowned for 
                    delivering uncompromising quality in service to our clients while empowering and 
                    uplifting the lives of our employees.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Mission */}
            <Card className="bg-white border-0 shadow-xl overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-[#408D4D] p-8">
                  <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-4">
                    <Target className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Our Mission</h3>
                </div>
                <div className="p-8">
                  <p className="text-gray-600 leading-relaxed text-lg">
                    Our mission is to provide exceptional logistics solutions that consistently exceed 
                    client expectations through efficiency, reliability, and innovation. We are committed 
                    to fostering growth and development, both for our clients and our employees.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#408D4D] font-semibold tracking-wider uppercase text-sm">Our Foundation</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1B4F5D] mt-3 mb-4">
              Core Values
            </h2>
            <p className="text-gray-600 text-lg">
              At CitiBiz Connections, our core values guide everything we do, ensuring we 
              consistently deliver exceptional service to our clients.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {coreValues.map((value, index) => {
              const Icon = iconMap[value.icon] || Shield;
              return (
                <Card 
                  key={value.id} 
                  className="text-center bg-white border-2 border-gray-100 hover:border-[#408D4D] shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <CardContent className="p-10">
                    <div className="w-20 h-20 mx-auto rounded-full bg-[#408D4D]/10 flex items-center justify-center mb-6">
                      <Icon className="text-[#408D4D]" size={40} />
                    </div>
                    <h3 className="text-2xl font-bold text-[#1B4F5D] mb-4">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CEO Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="lg:sticky lg:top-32">
              <div className="relative">
                <img
                  src="https://images.pexels.com/photos/7552373/pexels-photo-7552373.jpeg?auto=compress&w=600"
                  alt={ceoInfo.name}
                  className="rounded-2xl shadow-2xl w-full max-w-md"
                />
                <div className="absolute -bottom-6 -right-6 bg-[#1B4F5D] text-white p-6 rounded-xl shadow-xl">
                  <p className="font-bold">{ceoInfo.name}</p>
                  <p className="text-sm text-[#408D4D]">{ceoInfo.title}</p>
                </div>
              </div>
            </div>

            <div>
              <span className="text-[#408D4D] font-semibold tracking-wider uppercase text-sm">Leadership</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1B4F5D] mt-3 mb-6">
                Meet Our CEO
              </h2>
              
              <div className="prose prose-lg text-gray-600 mb-8">
                <p className="leading-relaxed mb-4">
                  Korku Sedemor Adjorlolo is a dynamic sales and logistics professional with over 
                  a decade of experience in shipping, logistics, and corporate operations across 
                  Ghana and West Africa.
                </p>
                <p className="leading-relaxed mb-4">
                  With a background in International Oil and Gas Management (MSc) and Psychology (BSc), 
                  he brings a strategic and people-focused approach to business development.
                </p>
                <p className="leading-relaxed">
                  Korku has held key roles at leading logistics firms such as McDan Shipping and 
                  Aramex Ghana, where he led high-impact sales teams, managed international partnerships, 
                  and executed large-scale projects including telecom supply for Ghana's digitization initiative.
                </p>
              </div>

              {/* Credentials */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow">
                  <GraduationCap className="text-[#408D4D]" size={24} />
                  <div>
                    <p className="text-sm text-gray-500">Education</p>
                    <p className="font-semibold text-[#1B4F5D] text-sm">MSc, BSc</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow">
                  <Award className="text-[#408D4D]" size={24} />
                  <div>
                    <p className="text-sm text-gray-500">Certifications</p>
                    <p className="font-semibold text-[#1B4F5D] text-sm">NEBOSH, IATA</p>
                  </div>
                </div>
              </div>

              {/* CEO Message */}
              <div className="bg-[#1B4F5D] rounded-2xl p-8 text-white">
                <div className="text-[#408D4D]/50 mb-4">
                  <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
                <p className="italic leading-relaxed mb-4">
                  Thank you for choosing CitiBiz Connections. We look forward to moving your 
                  business forward—smoothly, safely, and successfully.
                </p>
                <p className="text-[#408D4D] font-semibold">— Korku Sedemor Adjorlolo</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#1B4F5D]">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#408D4D] mb-2">
                  {stat.value}
                </div>
                <div className="text-white/80">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1B4F5D] mb-6">
              Partner With Us Today
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Experience the CitiBiz Connections difference. Let us handle your logistics 
              needs with expertise and care.
            </p>
            <Link to="/contact">
              <Button className="bg-[#408D4D] hover:bg-[#2D6636] text-white px-10 py-6 text-lg rounded-lg font-semibold">
                Get in Touch
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
